import { useState, useCallback } from 'react';
import axios from 'axios';
import { environment } from '../../environment';
import { ChangeDetectionResult, UpdateListingRequest, UpdateListingResponse } from './types';

interface UseDesignChangesResult {
    changes: ChangeDetectionResult | null;
    loading: boolean;
    updating: boolean;
    error: string | null;
    checkChanges: () => Promise<void>;
    updateListing: (selectedSteps: string[]) => Promise<UpdateListingResponse | null>;
    clearError: () => void;
}

/**
 * Hook for managing design change detection and listing updates
 * @param designId - The design ID to check for changes
 * @param authToken - Authentication token for API requests
 * @returns Change detection state and methods
 */
export const useDesignChanges = (
    designId: number | null,
    authToken: string | null
): UseDesignChangesResult => {
    const [changes, setChanges] = useState<ChangeDetectionResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /**
     * Checks for changes between current design state and release snapshot
     */
    const checkChanges = useCallback(async () => {
        if (!designId || !authToken) {
            setChanges(null);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await axios.get<ChangeDetectionResult>(
                `${environment.baseUrl}/api/v1/designs/release/${designId}/changes`,
                {
                    headers: {
                        'x-wallmates-auth': authToken
                    }
                }
            );

            setChanges(response.data);
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to check for changes';
            setError(errorMessage);
            console.error('[useDesignChanges] Error checking changes:', err);
        } finally {
            setLoading(false);
        }
    }, [designId, authToken]);

    /**
     * Updates the listing for selected steps
     * @param selectedSteps - Array of step keys to update (e.g., ['step_1_1', 'step_2_1'])
     * @returns Update response with success status and updated release data
     */
    const updateListing = useCallback(async (selectedSteps: string[]): Promise<UpdateListingResponse | null> => {
        if (!changes?.release?.id || !authToken) {
            setError('No release found or missing authentication');
            return null;
        }

        if (selectedSteps.length === 0) {
            setError('No steps selected for update');
            return null;
        }

        setUpdating(true);
        setError(null);

        try {
            const requestBody: UpdateListingRequest = {
                steps: selectedSteps
            };

            const response = await axios.post<UpdateListingResponse>(
                `${environment.baseUrl}/api/v1/designs/release/${changes.release.id}/update`,
                requestBody,
                {
                    headers: {
                        'x-wallmates-auth': authToken
                    }
                }
            );

            // Check if update requires RPE regeneration (steps 3.1, 3.2, 4.1)
            const requiresRPE = selectedSteps.some(step =>
                ['step_3_1', 'step_3_2', 'step_4_1'].includes(step)
            );

            if (requiresRPE && response.data.success) {
                console.log('[useDesignChanges] RPE regeneration triggered for selected steps');
                // TODO: Poll for RPE completion progress in future iteration
            }

            // Refresh changes after update
            await checkChanges();

            return response.data;
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to update listing';
            setError(errorMessage);
            console.error('[useDesignChanges] Error updating listing:', err);
            return null;
        } finally {
            setUpdating(false);
        }
    }, [changes, authToken, checkChanges]);

    /**
     * Clears the current error state
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        changes,
        loading,
        updating,
        error,
        checkChanges,
        updateListing,
        clearError
    };
};
